import axios from 'axios';
import { WS_APP_SUBPROTOCOL, WS_BEARER_SUBPROTOCOL_PREFIX } from '~/services/websocket/constants';
import { tokenGetter } from '~/utils/auth';
import { captureSession, subscribeSessionBoundary } from '~/utils/sessionBoundary';
import { Citation } from './types';

/**
 * Shape returned by sendMessageHttp. Mirrors the payload of ON_RECEIVE_MESSAGE
 * so the state machine can route REST responses through the same addBotMessage
 * reducer the WebSocket path uses. Any new field the REST/WS endpoints expose
 * should be added here AND to events.ts ReceiveMessageEvent.payload together.
 */
export interface HttpChatResponse {
  message: string;
  citations: Citation[];
  suggestedTopics: string[];
  isClarification: boolean;
  isPii: boolean;
  isRateLimited: boolean;
  sessionId?: string;
}

// REST API fallback. Sends session_id so multi-turn history works over REST
// (the REST path is stateless; the server uses session_id to assemble history).
//
// Returns a shape compatible with ReceiveMessageEvent so the state machine
// can dispatch ON_RECEIVE_MESSAGE and reuse the same display path as WS.
// `answer` is null when the server returned a clarifying question (rate
// limit, PII block, vague-query clarification); we fall back to
// clarifying_question so the user sees the server's real message.
export const sendMessageHttp = async ({
  input,
}: {
  input: { message: string; sessionId?: string };
}): Promise<HttpChatResponse> => {
  const body: { message: string; session_id?: string } = { message: input.message };
  if (input.sessionId) {
    body.session_id = input.sessionId;
  }
  const response = await axios.post('/api/v1/ai-chatbot/chat', body);
  const data = response.data ?? {};
  return {
    message: String(data.answer ?? data.clarifying_question ?? ''),
    citations: Array.isArray(data.citations) ? data.citations : [],
    suggestedTopics: Array.isArray(data.suggested_topics) ? data.suggested_topics : [],
    isClarification: Boolean(data.needs_clarification),
    isPii: Boolean(data.is_pii),
    isRateLimited: Boolean(data.is_rate_limited),
    sessionId: typeof data.session_id === 'string' ? data.session_id : input.sessionId,
  };
};

// Sockets this module has opened and not yet closed. The chat UI listens for the
// events they dispatch on `document`, which is shared by every account that uses the
// page, so a socket must stop speaking the moment its session ends, whatever the
// React tree or the chatbot actor are doing at that point.
const openSockets = new Set<WebSocket>();

const closeSocket = (socket: WebSocket) => {
  openSockets.delete(socket);
  socket.onmessage = null;
  try {
    socket.close();
  } catch (error) {
    console.debug('Error closing chatbot WebSocket:', error);
  }
};

subscribeSessionBoundary(() => {
  [...openSockets].forEach(closeSocket);
});

// WebSocket API
export const createWebSocketConnection = async ({
  signal,
}: { signal?: AbortSignal } = {}): Promise<WebSocket> => {
  const session = captureSession();
  const abandoned = () => !!signal?.aborted || !session.isCurrent();
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'localhost:8001';
  // `^https?:\/\/` already strips both http:// and https://; no second pass needed.
  const wsUrl = baseUrl.replace(/^https?:\/\//, '');
  const protocol = baseUrl.startsWith('https') ? 'wss' : 'ws';

  const token = await tokenGetter.getToken();
  if (abandoned()) throw new DOMException('Chatbot connection abandoned', 'AbortError');
  if (!token) {
    throw new Error('Authentication token not available');
  }

  // MCV-1442: carry Clerk JWT in a non-echoed auth subprotocol while also
  // offering a safe app protocol the server can select for browser handshake.
  const socket = new WebSocket(`${protocol}://${wsUrl}/api/ws`, [
    WS_APP_SUBPROTOCOL,
    `${WS_BEARER_SUBPROTOCOL_PREFIX}${token}`,
  ]);

  openSockets.add(socket);
  signal?.addEventListener('abort', () => closeSocket(socket), { once: true });

  return new Promise((resolve, reject) => {
    socket.onopen = () => {
      if (abandoned()) {
        closeSocket(socket);
        reject(new DOMException('Chatbot connection abandoned', 'AbortError'));
        return;
      }
      socket.onmessage = (event) => {
        // Every event names its socket, so the view can ignore any that is not its own.
        if (!openSockets.has(socket)) return;
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'chatbot.complete') {
            document.dispatchEvent(
              new CustomEvent('chatbot-message', {
                detail: {
                  socket,
                  message: data.text,
                  citations: data.citations || [],
                  sessionId: data.session_id,
                  jobId: data.job_id,
                  isFallback: data.is_fallback,
                  confidenceScore: data.confidence_score,
                },
              })
            );
          } else if (data.type === 'chatbot.question') {
            // Handle clarification request
            document.dispatchEvent(
              new CustomEvent('chatbot-message', {
                detail: {
                  socket,
                  message: data.text,
                  citations: [],
                  jobId: data.job_id,
                  isClarification: true,
                  suggestedTopics: data.suggestions,
                  isPii: data.is_pii,
                  isRateLimited: data.is_rate_limited,
                  sessionId: data.session_id,
                },
              })
            );
          } else if (data.type === 'chatbot.reset_confirmed') {
            // Server has rotated to a new session_id after reset — dispatch
            // a bot-message-less update so the client picks up the new id
            // for subsequent REST fallback requests.
            document.dispatchEvent(
              new CustomEvent('chatbot-session-update', {
                detail: { socket, sessionId: data.session_id },
              })
            );
          } else if (data.type === 'chatbot.typing') {
            document.dispatchEvent(
              new CustomEvent('chatbot-typing', {
                detail: { socket, status: data.status },
              })
            );
          } else if (data.type === 'chatbot.error') {
            console.debug('Dispatching error:', data.text);
            // Handle error message
            document.dispatchEvent(
              new CustomEvent('chatbot-message', {
                detail: {
                  socket,
                  message: data.text,
                  citations: [],
                  isError: true,
                  errorCode: data.error_code,
                  isRetryable: data.is_retryable,
                },
              })
            );
          }
        } catch (error) {
          console.debug('Error handling WebSocket message:', error);
        }
      };

      resolve(socket);
    };

    socket.onerror = (error) => {
      openSockets.delete(socket);
      reject(error);
    };
    socket.onclose = (event) => {
      openSockets.delete(socket);
      console.log('🔌 [WS] WebSocket closed:', event.code, event.reason);
    };
  });
};

export const sendMessageWs = async ({
  input,
}: {
  input: { message: string; socket: WebSocket; userInfoId?: number };
}): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // New WebSocket architecture expects: { type: "chatbot.message", text: "..." }
      const messageJson = JSON.stringify({
        type: 'chatbot.message',
        text: input.message,
      });
      input.socket.send(messageJson);

      resolve('Message sent successfully');
    } catch (error) {
      console.debug('Error sending WebSocket message:', error);
      reject(error);
    }
  });
};

export const disconnectWebSocket = async ({
  input,
}: {
  input: { socket: WebSocket };
}): Promise<void> => {
  closeSocket(input.socket);
};

export const sendResetMessage = async ({
  input,
}: {
  input: { socket: WebSocket };
}): Promise<void> => {
  // Type must be 'chatbot.reset' to match server routing (see websocket_router.py).
  const resetJson = JSON.stringify({ type: 'chatbot.reset' });
  input.socket.send(resetJson);
};
